/**
 * Gves an accordion element functionality. So called "Haitari menu" :3
 *
 * Template in stage: https://b99e.stage.crasman.cloud/admin/manage/scriptlets/scriptleteditor/page_url/subtemplate/show/JsAJRbsnTdGCgGUBJ83ozA
 * Demo: https://b99e.stage.crasman.cloud/admin/manage/scriptlets/scriptleteditor/page_url/subtemplate/show/nOiUMSs4SeuOdugxC9XLtw
 *
 */
export default class Accordion {

  /**
   * @constructor
   * @param {HTMLElement} element accordion element
   * @param {object} options control options passed to instance
   * @param {number} options.transitionTime time it takes to animate accordion opening or closing, defaults to 350ms
   * @param {object} options.accordionItems extra options for individual accordion items
   */
  constructor(element, options ) {
    this._element = element;
    this._options = {
      transitionTime: 350,
      ...options
    }
    this._findElements();
    this._attachEventListeners();
    this._handleOptions();
    this._dom.accordionItems.forEach( this.setAccordionItemHeight );
  }

  /**
   * Find elements
   * @private
   */
  _findElements() {
    const accordionItemElements = this._element.querySelectorAll('.accordion-item');

    const accordionItems = Array.from( accordionItemElements ).map( accordionItem => {
      return {
        accordionItem: accordionItem,
        accordionButton: accordionItem.querySelector('button.accordion-button'),
        accordionCollapse: accordionItem.querySelector('.accordion-collapse'),
        accordionCollapseContent: accordionItem.querySelector('.accordion-collapse-content'),
      }
    })

    this._dom = {
      accordionItems
    };
  }

  /**
   * Attach event listeners to elements
   * @private
   */
  _attachEventListeners() {
    this._dom.accordionItems.forEach( accordionItem => {
      accordionItem.accordionButton.addEventListener('click', this._onAccordionButtonPresses.bind(this) );
    });
    window.addEventListener('resize', this._onWindowResize.bind(this) );
  }

  /**
   * Handle options parameters
   * @private
   */
  _handleOptions() {
    if( this._options.accordionItems ) {

      Object.keys( this._options.accordionItems ).forEach( key => {
        const item = this._dom.accordionItems.find( item => item.accordionItem.dataset.id === key );
        const accordionItemOptions = this._options.accordionItems[key];

        //Accordion items added classlist
        if( item?.accordionItem ) {
          item.accordionItem.classList.add( ...accordionItemOptions.classes );
        }
      });
    }
  }

  /**
   * Called when accordion item button is pressed, open or closes the accordion item
   * @param {PointerEvent} event event object
   */
  _onAccordionButtonPresses(event) {
    const item = this._dom.accordionItems.find( accordionItem => accordionItem.accordionButton === event.target );

    if( item ) {
      const isOpening = !item.accordionItem.classList.contains('show');
      item.accordionButton.setAttribute('aria-expanded', isOpening ? 'true': 'false');

      if( isOpening ) {
        item.accordionItem.classList.add('show');
      }

      item.accordionCollapse.classList.toggle('collapse', true);

      if( isOpening ) {
        this.setAccordionItemHeight( item );
      } else {
        item.accordionCollapse.style.height = '0px';
      }

      //Do things after transition is over
      setTimeout(() => {
        item.accordionCollapse.classList.toggle('collapse', false);

        if( !isOpening ) {
          item.accordionItem.classList.remove('show');

        }

      }, this._options.transitionTime);
    }
  }

  /**
   * Set accordion-collapse item a height from the content element for amination purposes.
   * @param {object} item - accordion item object
   */
  setAccordionItemHeight( item ) {
    item.accordionCollapse.style.height = `${item.accordionCollapseContent.scrollHeight}px`;
  }

  /**
   * Called when a window is resized. If a viewport proportions changes, in case like user flip phone from portrait to landscape, there is a change that
   * opened container element is shorter than before. Because container has a fixed height it might look funny
   * when content overflows from the container. By removing height from the container we adjust to that situtation.
   */
  _onWindowResize() {
    this._dom.accordionItems.forEach( item => {
      item.accordionCollapse.style.removeProperty('height');
    });
  }

  /**
   * Instantiate Accordion class for each element in the DOM and bind functionality the them.
   * @param {HTMLElement[]} accordionElements accordion elements
   * @param {object} options control options
   */
  static init( accordionElements, options = {} ) {
    accordionElements.forEach( element => {
      new Accordion( element, options );
    })
  }
}

